import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface ReceiptSettings {
  receiptHeader: string
  receiptFooter: string
}

export function ReceiptSection() {
  const queryClient = useQueryClient()
  const canManage = useSession((state) => state.can)(Permission.ManageSettings)

  const [header, setHeader] = useState('')
  const [footer, setFooter] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const settings = useQuery({
    queryKey: ['settings', 'receipt'],
    queryFn: () => apiClient.get<ReceiptSettings>('/api/settings/receipt'),
  })

  useEffect(() => {
    if (settings.data) {
      setHeader(settings.data.receiptHeader)
      setFooter(settings.data.receiptFooter)
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: () =>
      apiClient.put('/api/settings/receipt', {
        header: header.trim() || null,
        footer: footer.trim() || null,
      }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      await queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        {t('A line above the store name and a line at the bottom of every receipt — both optional.')}
      </p>

      <div className="surface-raised rounded-[var(--radius-card)] border p-4">
        <div className="flex flex-col gap-4">
          <Input
            label={t('Header')}
            value={header}
            onChange={(event) => setHeader(event.target.value)}
            placeholder={t('Leave blank to print just the store name')}
          />
          <Input
            label={t('Footer')}
            value={footer}
            onChange={(event) => setFooter(event.target.value)}
            placeholder={t('e.g. Thank you')}
          />
        </div>

        {error && (
          <p role="alert" className="mt-4 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        {canManage ? (
          <Button className="mt-4" full onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save receipt text'}
          </Button>
        ) : (
          <p className="surface-sunken mt-4 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)]">
            {t('Only an administrator can change the receipt text.')}
          </p>
        )}
      </div>
    </div>
  )
}
